import React, { useState, useEffect, useMemo } from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import {
  ChevronLeft,
  Download,
  AlertCircle,
  FileText,
  Image as ImageIcon,
  Film,
  Music,
  File,
  Loader2,
} from "lucide-react";
import PropTypes from "prop-types";
import useRTLContext from "../../../../hooks/useRTLContext";
import { useTranslation } from "react-i18next";

const DocumentViewer = ({ documentUrl, isOpen, onClose }) => {
  const { isRTL, direction } = useRTLContext();
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Extract file info from URL
  const fileInfo = useMemo(() => {
    if (!documentUrl)
      return { extension: "", fileName: "", fileType: "unknown" };

    try {
      const url = new URL(documentUrl, window.location.origin);
      const pathname = url.pathname;
      let fileName = pathname.split("/").pop() || "";
      let extension = "";

      // If fileName contains an extension, extract it
      if (fileName && fileName.includes(".")) {
        extension = fileName.split(".").pop()?.toLowerCase() || "";

        // Remove timestamp prefix from filename (format: timestamp-filename)
        // Look for pattern: digits followed by dash, then remove it
        const timestampPattern = /^\d+-/;
        if (timestampPattern.test(fileName)) {
          fileName = fileName.replace(timestampPattern, "");
        }

        // Decode URL-encoded characters (like %20 for spaces)
        fileName = decodeURIComponent(fileName);
      } else {
        // Try to detect file type from URL patterns or content-type hints
        const urlLower = documentUrl.toLowerCase();
        if (urlLower.includes("pdf") || urlLower.includes(".pdf")) {
          extension = "pdf";
        } else if (
          urlLower.includes("image") ||
          urlLower.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)($|\?)/)
        ) {
          extension = "jpg"; // default image extension
        } else if (
          urlLower.includes("video") ||
          urlLower.match(/\.(mp4|avi|mov|wmv|webm)($|\?)/)
        ) {
          extension = "mp4"; // default video extension
        } else if (
          urlLower.includes("audio") ||
          urlLower.match(/\.(mp3|wav|ogg|aac)($|\?)/)
        ) {
          extension = "mp3"; // default audio extension
        }
      }

      // Determine file type category
      const imageTypes = [
        "jpg",
        "jpeg",
        "png",
        "gif",
        "webp",
        "svg",
        "bmp",
        "ico",
      ];
      const videoTypes = [
        "mp4",
        "avi",
        "mov",
        "wmv",
        "flv",
        "webm",
        "mkv",
        "ogv",
      ];
      const audioTypes = ["mp3", "wav", "ogg", "aac", "m4a", "wma", "flac"];
      const documentTypes = [
        "pdf",
        "doc",
        "docx",
        "xls",
        "xlsx",
        "ppt",
        "pptx",
        "txt",
        "rtf",
      ];

      let fileType = "unknown";
      if (extension === "pdf") fileType = "pdf";
      else if (imageTypes.includes(extension)) fileType = "image";
      else if (videoTypes.includes(extension)) fileType = "video";
      else if (audioTypes.includes(extension)) fileType = "audio";
      else if (documentTypes.includes(extension)) fileType = "document";

      // Generate appropriate display name
      let displayName = "";
      if (
        fileName &&
        fileName !== extension &&
        !fileName.startsWith("blob:") &&
        fileName.length > 3
      ) {
        displayName = fileName;
      } else {
        // Generate generic name based on file type
        switch (fileType) {
          case "pdf":
            displayName = "Document.pdf";
            break;
          case "image":
            displayName = "Image";
            break;
          case "video":
            displayName = "Video";
            break;
          case "audio":
            displayName = "Audio";
            break;
          case "document":
            displayName = "Document";
            break;
          default:
            displayName = "File";
        }
      }

      return { extension, fileName: displayName, fileType };
    } catch (error) {
      // Fallback for invalid URLs
      return { extension: "", fileName: "Document", fileType: "unknown" };
    }
  }, [documentUrl]);

  // Reset states when modal opens/closes or URL changes
  useEffect(() => {
    if (isOpen && documentUrl) {
      setIsLoading(true);
      setHasError(false);
    }
  }, [isOpen, documentUrl]);

  // Handle download functionality with better error handling and CORS support
  const handleDownload = async () => {
    if (!documentUrl || isDownloading) return;

    setIsDownloading(true);
    try {
      // Try direct fetch first
      let response;
      try {
        response = await fetch(documentUrl, {
          mode: "cors",
          credentials: "omit",
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
      } catch (fetchError) {
        // If CORS fails, try alternative approach
        console.warn(
          "Direct fetch failed, trying alternative download method:",
          fetchError
        );

        // Alternative: Open in new tab and let browser handle download
        const link = document.createElement("a");
        link.href = documentUrl;
        link.download = fileInfo.fileName;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileInfo.fileName;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Download error:", error);

      // Show user-friendly error message
      alert(
        t(
          "Download failed. The file might not be accessible or there might be a network issue."
        )
      );

      // As a last resort, open the URL in a new tab
      try {
        window.open(documentUrl, "_blank", "noopener,noreferrer");
      } catch (openError) {
        console.error("Failed to open file in new tab:", openError);
      }
    } finally {
      setIsDownloading(false);
    }
  };

  // Get appropriate icon for file type
  const getFileIcon = () => {
    switch (fileInfo.fileType) {
      case "image":
        return <ImageIcon className="w-5 h-5" />;
      case "video":
        return <Film className="w-5 h-5" />;
      case "audio":
        return <Music className="w-5 h-5" />;
      case "document":
      case "pdf":
        return <FileText className="w-5 h-5" />;
      default:
        return <File className="w-5 h-5" />;
    }
  };

  // Render content based on file type
  const renderContent = () => {
    if (hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-[400px] text-gray-500">
          <AlertCircle className="w-16 h-16 mb-4 text-red-400" />
          <p className="text-lg font-medium mb-2">
            {t("Unable to load document")}
          </p>
          <p className="text-sm text-center max-w-md">
            {t(
              "The document could not be displayed. You can try downloading it instead."
            )}
          </p>
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isDownloading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {isDownloading ? t("Downloading...") : t("Download")}
          </button>
        </div>
      );
    }

    switch (fileInfo.fileType) {
      case "image":
        return (
          <img
            src={documentUrl}
            alt="Document"
            className="max-w-full max-h-[50vh] sm:max-h-[60vh] md:max-h-[70vh] lg:max-h-[800px] object-contain"
            loading="lazy"
            onLoad={() => setIsLoading(false)}
            onError={() => {
              setIsLoading(false);
              setHasError(true);
            }}
          />
        );

      case "pdf":
        return (
          <iframe
            src={`${documentUrl}#toolbar=1&navpanes=1&scrollbar=1`}
            className="w-full h-[50vh] sm:h-[60vh] md:h-[70vh] lg:h-[800px]"
            title="PDF Viewer"
            loading="lazy"
            onLoad={() => setIsLoading(false)}
            onError={() => {
              setIsLoading(false);
              setHasError(true);
            }}
          />
        );

      case "video":
        return (
          <video
            src={documentUrl}
            controls
            className="max-w-full max-h-[50vh] sm:max-h-[60vh] md:max-h-[70vh] lg:max-h-[800px]"
            onLoadedData={() => setIsLoading(false)}
            onError={() => {
              setIsLoading(false);
              setHasError(true);
            }}
          >
            {t("Your browser does not support the video tag.")}
          </video>
        );

      case "audio":
        return (
          <div className="flex flex-col items-center justify-center h-[300px]">
            <Music className="w-16 h-16 mb-4 text-gray-400" />
            <audio
              src={documentUrl}
              controls
              className="w-full max-w-md"
              onLoadedData={() => setIsLoading(false)}
              onError={() => {
                setIsLoading(false);
                setHasError(true);
              }}
            >
              {t("Your browser does not support the audio tag.")}
            </audio>
          </div>
        );

      case "document":
        // Try to display in iframe, fallback to download option
        return (
          <div className="w-full">
            <iframe
              src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                documentUrl
              )}&embedded=true`}
              className="w-full h-[50vh] sm:h-[60vh] md:h-[70vh] lg:h-[800px]"
              title="Document Viewer"
              loading="lazy"
              onLoad={() => setIsLoading(false)}
              onError={() => {
                setIsLoading(false);
                setHasError(true);
              }}
            />
          </div>
        );

      default:
        return (
          <div className="flex flex-col items-center justify-center h-[400px] text-gray-500">
            <File className="w-16 h-16 mb-4 text-gray-400" />
            <p className="text-lg font-medium mb-2">
              {t("Preview not available")}
            </p>
            <p className="text-sm text-center max-w-md mb-4">
              {t(
                "This file type cannot be previewed. You can download it to view the content."
              )}
            </p>
            <button
              onClick={handleDownload}
              disabled={isDownloading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isDownloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {isDownloading ? t("Downloading...") : t("Download")}
            </button>
          </div>
        );
    }
  };

  if (!documentUrl) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-[9999]">
      <div className="fixed inset-0 bg-[#04080B59]" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center sm:p-2 md:p-4 overflow-y-auto">
        <div className="min-h-full w-full sm:w-auto flex items-center justify-center">
          <DialogPanel className="w-full sm:w-[90vw] md:w-[85vw] lg:w-[75vw] xl:w-[800px] max-h-[93vh] bg-white rounded-md overflow-y-auto scrollbar-hide my-auto">
            {/* Header */}
            <div className="flex items-center justify-between bg-[#F4F4F4] border-b border-[#EAECF0] p-2 sm:p-4">
              <button
                onClick={onClose}
                className="flex items-center gap-2 text-black hover:text-gray-800 transition-colors min-w-0 flex-1"
              >
                <ChevronLeft className="w-6 h-6 sm:w-7 sm:h-7 m-1 sm:m-2 bg-white rounded-md flex-shrink-0" />
                <div className="flex items-center gap-2 min-w-0">
                  {getFileIcon()}
                  <span className="font-bold text-lg sm:text-xl truncate">
                    {fileInfo.fileName}
                  </span>
                </div>
              </button>

              {/* Download button */}
              <button
                onClick={handleDownload}
                disabled={isDownloading}
                className="ml-4 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 flex-shrink-0"
                title={t("Download")}
              >
                {isDownloading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Download className="w-5 h-5" />
                )}
              </button>
            </div>

            {/* Content */}
            <div className="p-2 sm:p-4 flex justify-center relative">
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              )}
              {renderContent()}
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

DocumentViewer.propTypes = {
  documentUrl: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default DocumentViewer;
